import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Card,
  Typography,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Snackbar,
  Avatar,
  Chip,
  Divider,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowBack, Save, CloudUpload, Delete } from '@mui/icons-material';
import { teamMemberService } from '../services/api';
import { getAssetUrl } from '../config';

const ROLE_OPTIONS = [
  { value: 'founder', label: 'Founder' },
  { value: 'co_founder', label: 'Co-Founder' },
  { value: 'team_member', label: 'Team Member' },
];

function rowToForm(row) {
  return {
    name: row?.name || '',
    role_type: row?.role_type || 'team_member',
    designation: row?.designation || '',
    bio: row?.bio || '',
    email: row?.email || '',
    phone: row?.phone || '',
    linkedin_url: row?.linkedin_url || '',
    display_order: String(row?.display_order ?? 0),
    is_active: row?.is_active === 0 || row?.is_active === false ? '0' : '1',
  };
}

const TeamMemberEditPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [member, setMember] = useState(null);
  const [form, setForm] = useState(rowToForm(null));
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });
  const backPath =
    member?.role_type === 'team_member' ? '/admin/team-members' : '/admin/founders';

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await teamMemberService.getById(id);
        const data = res.data?.data ?? res.data;
        if (!data) {
          setToast({ open: true, message: 'Team member not found', type: 'error' });
          return;
        }
        setMember(data);
        setForm(rowToForm(data));
        setPhotoPreview(getAssetUrl(data.photo_url));
      } catch (e) {
        setToast({
          open: true,
          message: e.response?.data?.error || 'Failed to load team member',
          type: 'error',
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onPickPhoto = (file) => {
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target?.result || '');
    reader.readAsDataURL(file);
  };

  const buildFormData = () => {
    const fd = new FormData();
    if (photoFile) fd.append('photo', photoFile);
    fd.append('name', form.name.trim());
    fd.append('role_type', form.role_type);
    fd.append('designation', form.designation || '');
    fd.append('bio', form.bio || '');
    fd.append('email', form.email || '');
    fd.append('phone', form.phone || '');
    fd.append('linkedin_url', form.linkedin_url || '');
    fd.append('display_order', String(form.display_order === '' ? 0 : form.display_order));
    fd.append('is_active', form.is_active === '1' ? '1' : '0');
    return fd;
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setToast({ open: true, message: 'Name is required', type: 'error' });
      return;
    }
    try {
      setSaving(true);
      await teamMemberService.update(id, buildFormData());
      setToast({ open: true, message: 'Team member saved successfully', type: 'success' });
      setTimeout(() => navigate(backPath), 800);
    } catch (e) {
      setToast({
        open: true,
        message: e.response?.data?.error || e.message || 'Save failed',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${form.name || 'this member'}? This cannot be undone.`)) return;
    try {
      setSaving(true);
      await teamMemberService.delete(id);
      setToast({ open: true, message: 'Deleted', type: 'success' });
      setTimeout(() => navigate(backPath), 600);
    } catch (e) {
      setToast({ open: true, message: e.response?.data?.error || 'Delete failed', type: 'error' });
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!member) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">Team member not found.</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/admin/team-members')}>
          Back to Team
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="md">
        <Button startIcon={<ArrowBack />} onClick={() => navigate(backPath)} sx={{ mb: 2, color: '#2c5f2d' }}>
          Back to Team
        </Button>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Edit Team Member
            </Typography>
            <Typography variant="body2" sx={{ color: '#667085', mt: 0.5 }}>
              ID {member.id}
              {member.created_at && ` · Added ${new Date(member.created_at).toLocaleDateString()}`}
            </Typography>
          </Box>
          <Chip
            label={ROLE_OPTIONS.find((r) => r.value === member.role_type)?.label || member.role_type}
            sx={{ fontWeight: 600 }}
          />
        </Box>

        <Card sx={{ p: { xs: 2, md: 4 }, border: '1px solid #e8ecf1', mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Photo
          </Typography>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={4} sx={{ textAlign: 'center' }}>
              <Avatar
                src={photoPreview || undefined}
                sx={{ width: 160, height: 160, mx: 'auto', fontSize: 48, bgcolor: '#2c5f2d' }}
              >
                {form.name?.[0]}
              </Avatar>
            </Grid>
            <Grid item xs={12} sm={8}>
              <Button variant="outlined" component="label" startIcon={<CloudUpload />} sx={{ mb: 1 }}>
                Upload new photo
                <input type="file" hidden accept="image/jpeg,image/png,image/gif,image/webp" onChange={(e) => onPickPhoto(e.target.files?.[0])} />
              </Button>
              <Typography variant="caption" display="block" sx={{ color: '#667085' }}>
                JPEG, PNG, GIF, or WebP. Max 5MB. Leave unchanged to keep current photo.
              </Typography>
              {member.photo_url && !photoFile && (
                <Typography variant="caption" display="block" sx={{ color: '#667085', mt: 1, wordBreak: 'break-all' }}>
                  Current: {member.photo_url}
                </Typography>
              )}
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Profile details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Full name" name="name" value={form.name} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth select required label="Role" name="role_type" value={form.role_type} onChange={handleChange} SelectProps={{ native: true }}>
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Designation / title"
                name="designation"
                value={form.designation}
                onChange={handleChange}
                placeholder="e.g. Founder & CEO, Program Director"
                helperText="Shown under the name on the public Team page"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={5}
                label="Bio"
                name="bio"
                value={form.bio}
                onChange={handleChange}
                placeholder="Full biography shown on the Team page (founder/co-founder sections show full text)"
                helperText={`${(form.bio || '').length} characters`}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Contact & links
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="email" label="Email" name="email" value={form.email} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Phone" name="phone" value={form.phone} onChange={handleChange} placeholder="+91 ..." />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="LinkedIn profile URL"
                name="linkedin_url"
                value={form.linkedin_url}
                onChange={handleChange}
                placeholder="https://linkedin.com/in/..."
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Display settings
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Display order"
                name="display_order"
                value={form.display_order}
                onChange={handleChange}
                helperText="Lower numbers appear first within the same role"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth select label="Status" name="is_active" value={form.is_active} onChange={handleChange} SelectProps={{ native: true }}>
                <option value="1">Active (visible on website)</option>
                <option value="0">Inactive (hidden)</option>
              </TextField>
            </Grid>
          </Grid>
        </Card>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <Button color="error" variant="outlined" startIcon={<Delete />} onClick={handleDelete} disabled={saving}>
            Delete member
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" onClick={() => navigate(backPath)} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
              onClick={handleSave}
              disabled={saving}
              sx={{ backgroundColor: '#2c5f2d', '&:hover': { backgroundColor: '#1f4620' } }}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </Box>
        </Box>
      </Container>

      <Snackbar open={toast.open} autoHideDuration={5000} onClose={() => setToast((t) => ({ ...t, open: false }))}>
        <Alert severity={toast.type} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TeamMemberEditPage;
